// components/Admin/DeptOwnerMapPage.jsx — Manage Department → Multiple Owners mapping
import React, { useState } from "react";
import {
  Avatar, Badge, Button, Dialog, DialogActions, DialogBody, DialogContent,
  DialogSurface, DialogTitle, DialogTrigger, Field, Input, Select, Spinner, Text, Tooltip,
} from "@fluentui/react-components";
import {
  Add24Regular, ArrowSync24Regular, BuildingMultiple24Regular,
  Delete24Regular, Dismiss24Regular, Warning24Regular,
  CheckmarkCircle24Filled, Info24Regular, Flash24Regular,
} from "@fluentui/react-icons";
import { useDeptOwnerMap } from "../../hooks/useDeptOwnerMap";
import PeoplePicker from "../shared/PeoplePicker";

// ── LekOil default department → owner mapping ─────────────────────────────────

function emailToName(email) {
  const local = email.split("@")[0];
  return local.split(".").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
}

function owner(email) {
  return { name: emailToName(email), email, jobTitle: "", department: "" };
}

const LEKOIL_DEFAULT_MAP = {
  "PROJECT & ENGINEERING": [
    owner("Kunle.kareem@lekoil.com"),
    owner("Oluwasegun.odukoya@lekoil.com"),
    owner("Seni.odukoya@lekoil.com"),
  ],
  "SHES": [
    owner("osarumwense.guobadia@lekoil.com"),
    owner("Livinus.Oshiomegie@lekoil.com"),
  ],
  "OPERATION": [
    owner("ademola.adeyanju@lekoil.com"),
    owner("Clement.Akpe@lekoil.com"),
  ],
  "ADMIN 276": [
    owner("Abimbola.Adediji@lekoil.com"),
  ],
  "IT": [
    owner("Manfred.ezetendu@lekoil.com"),
    owner("Daniel.olorundare@lekoil.com"),
  ],
  "WELL ENGINEERING": [
    owner("Emejiri.alikor@lekoil.com"),
  ],
  "SUBSURFACE": [
    owner("cletus.idiagbor@lekoil.com"),
  ],
  "ADMIN OFFICE": [
    owner("busayo.ogungbemi@lekoil.com"),
  ],
  "HR": [
    owner("ifeanyi.ndukwe@lekoil.com"),
  ],
  "FINANCE": [
    owner("Omowunmi.chinwuba@lekoil.com"),
  ],
  "LEGAL": [
    owner("olufunmbi.kehinde@lekoil.com"),
  ],
  "CORPORATE COMMS": [
    owner("bolade.owoeye@lekoil.com"),
  ],
};

// ── Owner chip inside a row ────────────────────────────────────────────────────

function OwnerChip({ owner, onRemove, canRemove }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      background: "#F5F8FF", border: "1px solid #D6E0FF",
      borderRadius: 8, padding: "6px 10px",
    }}>
      <Avatar name={owner.name || owner.email} size={24} color="brand" />
      <div>
        <Text style={{ fontSize: 12, fontWeight: 600, color: "#001A3D", display: "block", lineHeight: 1.3 }}>
          {owner.name || "—"}
        </Text>
        {owner.jobTitle && (
          <Text style={{ fontSize: 10, color: "#888", display: "block", lineHeight: 1.2 }}>{owner.jobTitle}</Text>
        )}
        <Text style={{ fontSize: 11, color: "#0066FF" }}>{owner.email}</Text>
      </div>
      {canRemove && (
        <Tooltip content="Remove this owner" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss24Regular style={{ fontSize: 14 }} />}
            onClick={onRemove}
            style={{ color: "#D13438", minWidth: 0, padding: 3, marginLeft: 2 }}
          />
        </Tooltip>
      )}
    </div>
  );
}

// ── Row per department ────────────────────────────────────────────────────────

function MappingRow({ dept, owners, onAddOwner, onRemoveOwner, onDeleteDept }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  return (
    <tr
      style={{ borderBottom: "1px solid #F3F2F1" }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "#F8F9FF"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      {/* Dept badge */}
      <td style={{ padding: "14px 16px", verticalAlign: "top", width: 200 }}>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "#F0F4FF", color: "#0044BB",
          padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
        }}>
          <BuildingMultiple24Regular style={{ fontSize: 14 }} />
          {dept}
        </span>
      </td>

      {/* Owner chips */}
      <td style={{ padding: "14px 16px", verticalAlign: "top" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-start" }}>
          {owners.map((owner, i) => (
            <OwnerChip
              key={`${owner.email}-${i}`}
              owner={owner}
              onRemove={() => onRemoveOwner(dept, i)}
              canRemove
            />
          ))}
          <Button
            appearance="subtle"
            size="small"
            icon={<Add24Regular />}
            onClick={() => onAddOwner(dept)}
            style={{
              color: "#0066FF", border: "1px dashed #C8D6FF",
              borderRadius: 8, padding: "5px 12px", fontSize: 12,
            }}
          >
            Add Owner
          </Button>
        </div>
      </td>

      {/* Delete dept */}
      <td style={{ padding: "14px 16px", verticalAlign: "top", whiteSpace: "nowrap" }}>
        {confirmDelete ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Text style={{ fontSize: 12, color: "#D13438" }}>Remove dept?</Text>
            <Button size="small" appearance="primary" onClick={() => { onDeleteDept(dept); setConfirmDelete(false); }} style={{ background: "#D13438", minWidth: 0 }}>Yes</Button>
            <Button size="small" appearance="secondary" onClick={() => setConfirmDelete(false)}>No</Button>
          </div>
        ) : (
          <Tooltip content="Remove this department and all its owners" relationship="label">
            <Button appearance="subtle" size="small" icon={<Delete24Regular />} onClick={() => setConfirmDelete(true)} style={{ color: "#D13438" }} />
          </Tooltip>
        )}
      </td>
    </tr>
  );
}

// ── Add dialog (new dept OR add owner to existing dept) ────────────────────────

function MappingDialog({ open, onClose, onSave, targetDept, departments, existingDepts }) {
  // targetDept = null  → adding NEW department (choose dept + first owner)
  // targetDept = "X"   → adding owner to EXISTING dept (dept fixed)
  const isNewDept = !targetDept;

  const [dept, setDept]     = useState("");
  const [owner, setOwner]   = useState(null);
  const [error, setError]   = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      setDept(targetDept || "");
      setOwner(null);
      setError("");
    }
  }, [open, targetDept]);

  async function handleSave() {
    if (isNewDept && !dept)  { setError("Please select a department."); return; }
    if (!owner?.email)        { setError("You must select an owner from O365."); return; }
    if (isNewDept && existingDepts.includes(dept)) {
      setError(`"${dept}" already has owners. Use the Add Owner button on that row instead.`); return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave(dept || targetDept, {
        name: owner.name || "",
        email: owner.email,
        jobTitle: owner.jobTitle || "",
        department: owner.department || "",
      });
      onClose();
    } catch (e) {
      setError(e.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  // For new dept: only show depts not yet mapped
  const deptOptions = isNewDept
    ? departments.filter((d) => !existingDepts.includes(d))
    : [];

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface style={{ maxWidth: 520, width: "95vw" }}>
        <DialogBody>
          <DialogTitle>
            {isNewDept ? "Add Department Mapping" : `Add Owner — ${targetDept}`}
          </DialogTitle>
          <DialogContent>
            <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
              <Text style={{ fontSize: 13, color: "#605E5C", lineHeight: 1.6 }}>
                {isNewDept
                  ? "Map a department to its owners. Multiple people can be added after creation."
                  : `Add another person who should receive User Report emails for the "${targetDept}" department.`}
              </Text>

              {/* Dept selector (only for new dept) */}
              {isNewDept && (
                <Field
                  label="Department"
                  required
                  hint={departments.length === 0 ? "Could not load departments from O365 — type the name manually." : undefined}
                >
                  {departments.length > 0 ? (
                    <Select value={dept} onChange={(_, d) => setDept(d.value)}>
                      <option value="">Select a department…</option>
                      {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                    </Select>
                  ) : (
                    <Input
                      value={dept}
                      onChange={(_, d) => setDept(d.value.toUpperCase())}
                      placeholder="e.g. WELL ENGINEERING"
                    />
                  )}
                </Field>
              )}

              {/* Owner picker */}
              <PeoplePicker
                label={isNewDept ? "First Owner" : "Owner to Add"}
                required
                value={owner}
                onChange={setOwner}
                placeholder="Search O365 for the owner…"
              />

              {owner && (
                <div style={{ background: "#F5F8FF", border: "1px solid #D6E8FF", borderRadius: 10, padding: "12px 14px", display: "flex", gap: 10, alignItems: "center" }}>
                  <Avatar name={owner.name || owner.email} size={36} color="brand" />
                  <div>
                    <Text style={{ fontWeight: 700, color: "#001A3D", fontSize: 13, display: "block" }}>{owner.name}</Text>
                    {owner.jobTitle && <Text style={{ fontSize: 11, color: "#555", display: "block" }}>{owner.jobTitle}</Text>}
                    <Text style={{ fontSize: 12, color: "#0066FF" }}>{owner.email}</Text>
                  </div>
                </div>
              )}

              {error && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FDE7E9", borderRadius: 8, color: "#D13438", fontSize: 13 }}>
                  <Warning24Regular style={{ flexShrink: 0 }} />{error}
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" disabled={saving}>Cancel</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              onClick={handleSave}
              disabled={saving || !owner?.email || (isNewDept && !dept.trim())}
              style={{ background: "linear-gradient(135deg,#0066FF,#002D7A)" }}
            >
              {saving ? <Spinner size="tiny" /> : isNewDept ? "Add Mapping" : "Add Owner"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function DeptOwnerMapPage() {
  const { map, fileExists, departments, loading, error: loadError, saveMap } = useDeptOwnerMap();
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [targetDept, setTargetDept]     = useState(null); // null = new dept, string = add owner to existing
  const [saving, setSaving]             = useState(false);
  const [saveError, setSaveError]       = useState(null);
  const [saveSuccess, setSaveSuccess]   = useState(false);
  const [seedConfirm, setSeedConfirm]   = useState(false);

  const entries = Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  const existingDepts = Object.keys(map);

  function openAddDept() { setTargetDept(null); setDialogOpen(true); }
  function openAddOwner(dept) { setTargetDept(dept); setDialogOpen(true); }

  async function handleSave(dept, owner) {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const currentOwners = map[dept] || [];
      // Prevent duplicate emails
      if (currentOwners.some((o) => o.email === owner.email)) {
        throw new Error(`${owner.email} is already an owner of ${dept}.`);
      }
      const newMap = { ...map, [dept]: [...currentOwners, owner] };
      await saveMap(newMap);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      setSaveError(e.message);
      throw e;
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveOwner(dept, index) {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = (map[dept] || []).filter((_, i) => i !== index);
      const newMap = { ...map };
      if (updated.length === 0) {
        delete newMap[dept];
      } else {
        newMap[dept] = updated;
      }
      await saveMap(newMap);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteDept(dept) {
    setSaving(true);
    setSaveError(null);
    try {
      const newMap = { ...map };
      delete newMap[dept];
      await saveMap(newMap);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSeedDefaults() {
    setSeedConfirm(false);
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      // Merge: for each default dept, keep existing owners and append any new ones not already present
      const merged = { ...map };
      Object.entries(LEKOIL_DEFAULT_MAP).forEach(([dept, defaultOwners]) => {
        const existing = merged[dept] || [];
        const existingEmails = new Set(existing.map((o) => o.email.toLowerCase()));
        const toAdd = defaultOwners.filter((o) => !existingEmails.has(o.email.toLowerCase()));
        merged[dept] = [...existing, ...toAdd];
      });
      await saveMap(merged);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const totalOwners = entries.reduce((sum, [, owners]) => sum + owners.length, 0);

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <BuildingMultiple24Regular style={{ color: "#0066FF", fontSize: 28 }} />
            <Text size={600} weight="bold" style={{ color: "#001A3D" }}>Department Owner Mapping</Text>
          </div>
          <Text style={{ color: "#605E5C", fontSize: 14 }}>
            Map departments to one or more owners. All owners receive User Report emails for their department.
          </Text>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {!seedConfirm ? (
            <Button
              appearance="outline"
              icon={<Flash24Regular />}
              onClick={() => setSeedConfirm(true)}
              disabled={loading || saving}
              style={{ color: "#006E6E", borderColor: "#006E6E" }}
            >
              Load LekOil Defaults
            </Button>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#FFF4EC", border: "1px solid #F7C59F", borderRadius: 8, padding: "6px 14px" }}>
              <Warning24Regular style={{ color: "#E65C00", fontSize: 16 }} />
              <Text style={{ fontSize: 13, color: "#E65C00" }}>
                {entries.length > 0 ? "Merge defaults with existing mapping?" : "Seed all LekOil default owners?"}
              </Text>
              <Button size="small" appearance="primary" onClick={handleSeedDefaults} style={{ background: "#006E6E", minWidth: 0 }}>
                {saving ? <Spinner size="tiny" /> : "Confirm"}
              </Button>
              <Button size="small" appearance="secondary" onClick={() => setSeedConfirm(false)}>Cancel</Button>
            </div>
          )}
          <Button
            appearance="primary"
            icon={<Add24Regular />}
            onClick={openAddDept}
            disabled={loading}
            style={{ background: "linear-gradient(135deg,#0066FF,#002D7A)" }}
          >
            Add Department
          </Button>
        </div>
      </div>

      {/* File status banner */}
      {!loading && fileExists === false && (
        <div style={{ background: "#EBF3FF", border: "1px solid #C8D6FF", borderRadius: 10, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "flex-start", gap: 10 }}>
          <Info24Regular style={{ color: "#005A9E", flexShrink: 0, marginTop: 1 }} />
          <div>
            <Text style={{ fontWeight: 700, color: "#005A9E", fontSize: 13, display: "block" }}>Mapping file not found in SharePoint</Text>
            <Text style={{ fontSize: 12, color: "#004578", display: "block", marginTop: 2 }}>
              <strong>LekOil_DeptOwnerMap.json</strong> will be created automatically in your site's Documents library when you add the first mapping.
            </Text>
          </div>
        </div>
      )}

      {!loading && fileExists === true && (
        <div style={{ background: "#F0F8F0", border: "1px solid #C3E6C3", borderRadius: 10, padding: "12px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <CheckmarkCircle24Filled style={{ color: "#107C10", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <Text style={{ fontWeight: 600, color: "#107C10", fontSize: 13 }}>
              <strong>LekOil_DeptOwnerMap.json</strong> — {entries.length} department{entries.length !== 1 ? "s" : ""}, {totalOwners} owner{totalOwners !== 1 ? "s" : ""} total
            </Text>
          </div>
          {saving && <Spinner size="tiny" />}
          {saveSuccess && !saving && <Badge appearance="tint" color="success">Saved</Badge>}
        </div>
      )}

      {/* Load error */}
      {loadError && (
        <div style={{ background: "#FDE7E9", border: "1px solid #F9C9CB", borderRadius: 10, padding: "12px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <Warning24Regular style={{ color: "#D13438" }} />
          <Text style={{ fontSize: 13, color: "#D13438" }}>Failed to load mapping: {loadError}</Text>
          <Button appearance="subtle" size="small" icon={<ArrowSync24Regular />} onClick={() => window.location.reload()} style={{ marginLeft: "auto", color: "#D13438" }}>Retry</Button>
        </div>
      )}

      {/* Save error */}
      {saveError && (
        <div style={{ background: "#FDE7E9", border: "1px solid #F9C9CB", borderRadius: 10, padding: "12px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <Warning24Regular style={{ color: "#D13438" }} />
          <Text style={{ fontSize: 13, color: "#D13438" }}>Save failed: {saveError}</Text>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
          <Spinner size="large" label="Loading mapping from SharePoint…" />
        </div>
      ) : entries.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #EDEBE9", borderRadius: 14, padding: "60px 40px", textAlign: "center" }}>
          <BuildingMultiple24Regular style={{ fontSize: 48, color: "#C8D6FF", marginBottom: 12 }} />
          <Text size={400} weight="semibold" style={{ color: "#605E5C", display: "block", marginBottom: 8 }}>No department mappings yet</Text>
          <Text style={{ color: "#999", fontSize: 13, display: "block", marginBottom: 20 }}>
            Add mappings to automatically populate owners when a department is selected on an activity, and to route User Report emails.
          </Text>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <Button appearance="primary" icon={<Add24Regular />} onClick={openAddDept} style={{ background: "linear-gradient(135deg,#0066FF,#002D7A)" }}>
              Add First Mapping
            </Button>
            <Button
              appearance="outline"
              icon={<Flash24Regular />}
              onClick={() => setSeedConfirm(true)}
              style={{ color: "#006E6E", borderColor: "#006E6E" }}
            >
              Load LekOil Defaults
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #EDEBE9", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #EDEBE9", background: "#F8F9FF", display: "flex", alignItems: "center", gap: 8 }}>
            <Text style={{ fontSize: 11, fontWeight: 700, color: "#888", letterSpacing: "0.5px" }}>
              {entries.length} DEPARTMENT{entries.length !== 1 ? "S" : ""} · {totalOwners} OWNER{totalOwners !== 1 ? "S" : ""}
            </Text>
            {saving && <Spinner size="tiny" style={{ marginLeft: "auto" }} />}
            {saveSuccess && !saving && (
              <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#107C10", fontWeight: 600 }}>
                <CheckmarkCircle24Filled style={{ fontSize: 14 }} /> Saved to SharePoint
              </span>
            )}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#F8F9FF", borderBottom: "1px solid #EDEBE9" }}>
                {["Department", "Owners (receive User Reports)", ""].map((h) => (
                  <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "#888", letterSpacing: "0.5px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map(([dept, owners]) => (
                <MappingRow
                  key={dept}
                  dept={dept}
                  owners={owners}
                  onAddOwner={openAddOwner}
                  onRemoveOwner={handleRemoveOwner}
                  onDeleteDept={handleDeleteDept}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info box */}
      {!loading && (
        <div style={{ marginTop: 24, background: "#FAFBFF", border: "1px solid #E3EEFF", borderRadius: 10, padding: "14px 18px" }}>
          <Text style={{ fontSize: 12, color: "#555", lineHeight: 1.8 }}>
            <strong>How it works:</strong> This mapping is stored as{" "}
            <code style={{ background: "#EEF2FF", padding: "1px 5px", borderRadius: 4 }}>LekOil_DeptOwnerMap.json</code>{" "}
            in your SharePoint site's Documents library.
            When a department is selected on an activity, the <em>first</em> owner is auto-filled on the form.
            When a User Report is sent, <em>all</em> owners for that department receive the report email.
          </Text>
        </div>
      )}

      {/* Add dialog */}
      <MappingDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        targetDept={targetDept}
        departments={departments}
        existingDepts={existingDepts}
      />
    </div>
  );
}
